import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SwipeStatus } from "components/bookShuffle/bookShuffleTypes";

/** This is the data that is shared between handlers for mouse and touch events */
interface SharedCursorEventData {
  pageX: number;
  pageY: number;
}

/** The methods by which a user can decide a swipe status */
export type SwipeStatusAction = "click" | "swipe";

/** All the data necessary for managing swipe interaction & animation */
export interface SwipeData {
  tabIsVisible: boolean;
  mouseStartX: number;
  mouseStartY: number;
  mouseCurrentX: number;
  mouseCurrentY: number;
  mousePrevX: number;
  mousePrevY: number;
  mouseVelocityX: number;
  mouseVelocityY: number;
  mouseDx: number;
  mouseDy: number;
  componentVelocityX: number;
  componentVelocityY: number;
  componentStartX: number;
  componentStartY: number;
  componentCurrentX: number;
  componentCurrentY: number;
  componentWithinViewport: boolean;
  swipeStatus: SwipeStatus;
  swipeStatusDecisionDistancePx: number;
  swipeStatusFinalized: boolean;
  componentOpacity: number;
  mouseDown: boolean;
  timestampPrev: number;
  timestampCurrent: number;
}

const DefaultSwipeData = Object.freeze<SwipeData>({
  tabIsVisible: true,
  mouseStartX: 0,
  mouseStartY: 0,
  mouseCurrentX: 0,
  mouseCurrentY: 0,
  mousePrevX: 0,
  mousePrevY: 0,
  mouseVelocityX: 0,
  mouseVelocityY: 0,
  mouseDx: 0,
  mouseDy: 0,
  componentVelocityX: 0,
  componentVelocityY: 0,
  componentStartX: 0,
  componentStartY: 0,
  componentCurrentX: 0,
  componentCurrentY: 0,
  componentWithinViewport: true,
  swipeStatus: SwipeStatus.UNDECIDED,
  swipeStatusDecisionDistancePx: 0,
  swipeStatusFinalized: false,
  componentOpacity: 1,
  mouseDown: false,
  timestampPrev: 0,
  timestampCurrent: 0,
});

export interface UseSwipeParameters {
  children?: ReactNode;
  className?: string;

  /** This runs when a user begins a drag action */
  onSwipeStart?: (swipeData: SwipeData) => void;
  /** This runs when a user ends a drag action */
  onSwipeEnd?: (swipeData: SwipeData) => void;
  /** This runs when an accepted/rejected component is no longer visible */
  onExitComplete?: (swipeData: SwipeData) => void;
  /** This runs once the card has been committed to either ACCEPTED or REJECTED status */
  onStatusDecision?: (
    swipeStatus: SwipeStatus,
    swipeStatusAction: SwipeStatusAction
  ) => void;
  /** Runs once an animation cycle has completed (for hooking into current status) */
  onAnimateCycleComplete?: (swipeData: SwipeData) => void;

  /**
   * Returns how far the component must be dragged for the status decision to be considered
   * final when the component is let go.
   */
  getSwipeStatusDecisionDistance?: () => number;
  /**
   * How fast the component must be swiped to be considered accepted or rejected
   * Velocity here is measured as px per mouse/touch event.
   */
  swipeStatusDecisionVelocityPx?: number;
  /** How far the component has to move to before it starts to fade out */
  swipeFadeDistanceStartPx?: number;
  /** How far the component has to move to before opacity is done fading out once accepted or rejected */
  swipeFadeDistanceEndPx?: number;
  /** Controls how fast the component bounces back to center after being released */
  springiness?: number;
  /**
   * How fast the component flies out horizontally after being accepted/rejected.
   * This value gets reversed depending on if the swipe is of status ACCEPTED or REJECTED.
   */
  flyOutSpeedXPx?: number;
  /**
   * How fast the component flies out vertically after being accepted/rejected.
   * positive = flying down, negative = flying up.
   */
  flyOutSpeedYPx?: number;
  /** Dampens velocity. Without this, the component will bounce around the center indefinitely */
  frictionBaseMultiplier?: number;
  /** The pixel width at which friction starts increasing */
  frictionIncreaseBreakpointPx?: number;
  /** The pixel width at which friction reaches its max */
  frictionMaxBreakpointPx?: number;
  /** The friction adjustment that should be reached at `frictionIncreaseBreakpointPx` */
  frictionAdjustmentMaxPx?: number;
  /** The friction adjustment that should be reached at `frictionMaxBreakpointPx` */
  frictionAdjustmentMinPx?: number;
  /** How much the component turns as it is moved left-to-right, relative to distance traveled */
  rotationMultiplier?: number;
  /** Limits how fast the component can move in any particular direction */
  maxVelocityPx?: number;
  /**
   * This number is multiplied with the number of ms elapsed since last animation frame.
   * The result is then multiplied with velocity.
   * This helps improve the consistency of animations when frame rates differ.
   */
  timestampMultiplier?: number;
  /** Setting this value to `true` causes `useSwipe` to set the background of the element based on its current `SwipeStatus` */
  debugSwipeStatusDecisionThreshold?: boolean;
}

const DefaultArguments = {
  swipeStatusDecisionVelocityPx: 20,
  swipeFadeDistanceStartPx: 40,
  swipeFadeDistanceEndPx: 200,
  springiness: 0.005,
  flyOutSpeedXPx: 75,
  flyOutSpeedYPx: 0,
  frictionBaseMultiplier: 0.7,
  frictionIncreaseBreakpointPx: 400,
  frictionMaxBreakpointPx: 1400,
  frictionAdjustmentMaxPx: 0,
  frictionAdjustmentMinPx: -0.2,
  rotationMultiplier: -0.01,
  maxVelocityPx: 100,
  timestampMultiplier: 0.02,
  debugSwipeStatusDecisionThreshold: false,
  getSwipeStatusDecisionDistance: () => (window ? window.innerWidth / 3 : 100),
};

export function useSwipe<E extends HTMLElement>({
  onSwipeEnd,
  onSwipeStart,
  onExitComplete,
  onStatusDecision,
  onAnimateCycleComplete,
  swipeStatusDecisionVelocityPx = DefaultArguments.swipeStatusDecisionVelocityPx,
  swipeFadeDistanceStartPx = DefaultArguments.swipeFadeDistanceStartPx,
  swipeFadeDistanceEndPx = DefaultArguments.swipeFadeDistanceEndPx,
  springiness = DefaultArguments.springiness,
  flyOutSpeedXPx = DefaultArguments.flyOutSpeedXPx,
  flyOutSpeedYPx = DefaultArguments.flyOutSpeedYPx,
  frictionBaseMultiplier = DefaultArguments.frictionBaseMultiplier,
  frictionIncreaseBreakpointPx = DefaultArguments.frictionIncreaseBreakpointPx,
  frictionMaxBreakpointPx = DefaultArguments.frictionMaxBreakpointPx,
  frictionAdjustmentMaxPx = DefaultArguments.frictionAdjustmentMaxPx,
  frictionAdjustmentMinPx = DefaultArguments.frictionAdjustmentMinPx,
  rotationMultiplier = DefaultArguments.rotationMultiplier,
  maxVelocityPx = DefaultArguments.maxVelocityPx,
  timestampMultiplier = DefaultArguments.timestampMultiplier,
  debugSwipeStatusDecisionThreshold = DefaultArguments.debugSwipeStatusDecisionThreshold,
  getSwipeStatusDecisionDistance = DefaultArguments.getSwipeStatusDecisionDistance,
}: UseSwipeParameters = DefaultArguments) {
  const elementRef: MutableRefObject<E | null> = useRef<E>(null);
  const swipeDataRef = useRef({
    ...DefaultSwipeData,
  });
  const NULL_ANIMATION_HANDLE = 0;
  const animationHandleRef = useRef(NULL_ANIMATION_HANDLE);
  const intersectionObserverRef = useRef(
    new IntersectionObserver((entries) => {
      const entry = entries.find(
        (entry) => entry.target === elementRef.current
      );
      if (!entry) return;
      swipeDataRef.current.componentWithinViewport = entry.isIntersecting;
    })
  );
  const [swiping, setSwiping] = useState(false);

  /** Used for both mobile and desktop interactions */
  const handleCursorDown = useCallback(
    <E extends SharedCursorEventData & { target: unknown }>(e: E) => {
      // should not be able to drag when user clicks our touches button
      if ((e?.target as HTMLElement)?.closest?.("button")) return;

      const { pageX, pageY } = e;
      swipeDataRef.current.mouseDown = true;
      swipeDataRef.current.mouseStartX = pageX;
      swipeDataRef.current.mouseStartY = pageY;
      swipeDataRef.current.mousePrevX = pageX;
      swipeDataRef.current.mousePrevY = pageY;
      swipeDataRef.current.mouseVelocityX = 0;
      swipeDataRef.current.mouseVelocityY = 0;
      swipeDataRef.current.mouseCurrentX = pageX;
      swipeDataRef.current.mouseCurrentY = pageY;
      swipeDataRef.current.mouseDx = 0;
      swipeDataRef.current.mouseDy = 0;

      setSwiping(true);
      onSwipeStart?.(swipeDataRef.current);
    },
    [onSwipeStart]
  );

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      handleCursorDown(touch);
    },
    [handleCursorDown]
  );

  /** Shared for both desktop and mobile interactions */
  const handleCursorMove = useCallback(
    ({ pageX, pageY }: SharedCursorEventData) => {
      if (!swipeDataRef.current.mouseDown) return;

      swipeDataRef.current.mousePrevX = swipeDataRef.current.mouseCurrentX;
      swipeDataRef.current.mousePrevY = swipeDataRef.current.mouseCurrentY;
      swipeDataRef.current.mouseCurrentX = pageX;
      swipeDataRef.current.mouseCurrentY = pageY;
      swipeDataRef.current.mouseVelocityX =
        swipeDataRef.current.mouseCurrentX - swipeDataRef.current.mousePrevX;
      swipeDataRef.current.mouseVelocityY =
        swipeDataRef.current.mouseCurrentY - swipeDataRef.current.mousePrevY;
      swipeDataRef.current.mouseDx =
        swipeDataRef.current.mouseCurrentX - swipeDataRef.current.mouseStartX;
      swipeDataRef.current.mouseDy =
        swipeDataRef.current.mouseCurrentY - swipeDataRef.current.mouseStartY;

      // the swipe status is set here, but is only finalized on mouse up
      if (
        swipeDataRef.current.mouseDx >
        swipeDataRef.current.swipeStatusDecisionDistancePx
      ) {
        swipeDataRef.current.swipeStatus = SwipeStatus.ACCEPTED;
      } else if (
        swipeDataRef.current.mouseDx <
        -swipeDataRef.current.swipeStatusDecisionDistancePx
      ) {
        swipeDataRef.current.swipeStatus = SwipeStatus.REJECTED;
      } else {
        swipeDataRef.current.swipeStatus = SwipeStatus.UNDECIDED;
      }
    },
    []
  );

  /** Shared for both desktop and mobile interactions */
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      handleCursorMove(touch);
    },
    [handleCursorMove]
  );

  /**
   * If swipe status has not already been finalized, sets the elements
   * SwipeStatus and emits the result as an event
   */
  const setAndEmitSwipeStatus = useCallback(
    (swipeStatus: SwipeStatus, swipeStatusAction: SwipeStatusAction) => {
      if (swipeDataRef.current.swipeStatusFinalized) return;

      swipeDataRef.current.swipeStatus = swipeStatus;
      swipeDataRef.current.swipeStatusFinalized = true;
      onStatusDecision?.(swipeDataRef.current.swipeStatus, swipeStatusAction);
    },
    [onStatusDecision]
  );

  const handleCursorUp = useCallback(() => {
    swipeDataRef.current.mouseDown = false;
    swipeDataRef.current.componentVelocityX =
      swipeDataRef.current.mouseVelocityX;
    swipeDataRef.current.componentVelocityY =
      swipeDataRef.current.mouseVelocityY;

    onSwipeEnd?.(swipeDataRef.current);
    setSwiping(false);

    const SWIPE_STATUS_ACTION = "swipe";

    // letting go of component outside of the boundary zones finalizes status decision
    if (
      swipeDataRef.current.componentCurrentX >
      swipeDataRef.current.swipeStatusDecisionDistancePx
    ) {
      setAndEmitSwipeStatus(SwipeStatus.ACCEPTED, SWIPE_STATUS_ACTION);
    } else if (
      swipeDataRef.current.componentCurrentX <
      -swipeDataRef.current.swipeStatusDecisionDistancePx
    ) {
      setAndEmitSwipeStatus(SwipeStatus.REJECTED, SWIPE_STATUS_ACTION);
    }

    // swiping fast enough can trigger accept/reject
    if (
      swipeDataRef.current.componentVelocityX > swipeStatusDecisionVelocityPx
    ) {
      setAndEmitSwipeStatus(SwipeStatus.ACCEPTED, SWIPE_STATUS_ACTION);
    } else if (
      swipeDataRef.current.componentVelocityX < -swipeStatusDecisionVelocityPx
    ) {
      setAndEmitSwipeStatus(SwipeStatus.REJECTED, SWIPE_STATUS_ACTION);
    }
  }, [onSwipeEnd, setAndEmitSwipeStatus, swipeStatusDecisionVelocityPx]);

  /** Sets opacity data to decrease as element is dragged side-to-side */
  const updateElementOpacityData = useCallback(() => {
    const decimalFadeAmount =
      (Math.abs(swipeDataRef.current.componentCurrentX) -
        swipeFadeDistanceStartPx) /
      swipeFadeDistanceEndPx;
    const clampedDecimalFadeAmount = Math.max(
      Math.min(decimalFadeAmount, 0.7),
      0
    );
    swipeDataRef.current.componentOpacity = 1 - clampedDecimalFadeAmount;
  }, [swipeFadeDistanceStartPx, swipeFadeDistanceEndPx]);

  const updateSwipeThresholdData = useCallback(() => {
    /** How far from center the component must be from center to be considered accepted or rejected */
    swipeDataRef.current.swipeStatusDecisionDistancePx =
      getSwipeStatusDecisionDistance();
  }, [getSwipeStatusDecisionDistance]);

  /** Keeps element and mouse position aligned */
  const moveElementToBeUnderCursor = useCallback(() => {
    // round to integer to prevent subpixel rendering
    swipeDataRef.current.componentCurrentX = Math.round(
      swipeDataRef.current.componentStartX + swipeDataRef.current.mouseDx
    );
    // round to integer to prevent subpixel rendering
    swipeDataRef.current.componentCurrentY = Math.round(
      swipeDataRef.current.componentStartY + swipeDataRef.current.mouseDy
    );
  }, []);

  /** Returns element back to its starting position */
  const moveElementTowardsCenter = useCallback(
    (dt: number) => {
      // update velocity based on spring strength
      const distanceToCenterX =
        swipeDataRef.current.componentCurrentX -
        swipeDataRef.current.componentStartX;
      const distanceToCenterY =
        swipeDataRef.current.componentCurrentY -
        swipeDataRef.current.componentStartY;

      // This math uses Hooke's law (for determining force of spring when under tension)
      // @see https://en.wikipedia.org/wiki/Hooke%27s_law
      // basically: restoring force = stretched distance * springiness * -1;

      const springForceX =
        swipeDataRef.current.componentCurrentX *
        distanceToCenterX *
        springiness *
        -Math.sign(swipeDataRef.current.componentCurrentX);
      const springForceY =
        swipeDataRef.current.componentCurrentY *
        distanceToCenterY *
        springiness *
        -Math.sign(swipeDataRef.current.componentCurrentY);

      // update velocity with spring force
      swipeDataRef.current.componentVelocityX =
        swipeDataRef.current.componentVelocityX + springForceX;
      swipeDataRef.current.componentVelocityY =
        swipeDataRef.current.componentVelocityY + springForceY;

      /** Makes friction increase as screen size increases */
      const frictionScreenSizeAdjustment = Math.min(
        Math.max(
          (window.innerWidth - frictionIncreaseBreakpointPx) /
            ((frictionMaxBreakpointPx - frictionIncreaseBreakpointPx) /
              frictionAdjustmentMinPx),
          frictionAdjustmentMinPx
        ),
        frictionAdjustmentMaxPx
      );

      const finalFrictionForce =
        frictionBaseMultiplier + frictionScreenSizeAdjustment;

      // update velocity with friction
      swipeDataRef.current.componentVelocityX *= finalFrictionForce;
      swipeDataRef.current.componentVelocityY *= finalFrictionForce;

      // update position based on velocity and most recent timestamp:
      // helps keep animations more consistent across varying frame rates
      const positionIncrementX =
        swipeDataRef.current.componentVelocityX * dt * timestampMultiplier;
      const positionIncrementY =
        swipeDataRef.current.componentVelocityY * dt * timestampMultiplier;

      // limit max final velocity
      const positionIncrementXClamped =
        Math.min(Math.abs(positionIncrementX), maxVelocityPx) *
        Math.sign(positionIncrementX);
      const positionIncrementYClamped =
        Math.min(Math.abs(positionIncrementY), maxVelocityPx) *
        Math.sign(positionIncrementY);

      swipeDataRef.current.componentCurrentX += positionIncrementXClamped;
      swipeDataRef.current.componentCurrentY += positionIncrementYClamped;
    },
    [
      frictionAdjustmentMaxPx,
      frictionAdjustmentMinPx,
      frictionBaseMultiplier,
      frictionIncreaseBreakpointPx,
      frictionMaxBreakpointPx,
      maxVelocityPx,
      springiness,
      timestampMultiplier,
    ]
  );

  /** Incrementally moves element off the screen after SwipeStatus has been decided */
  const moveElementOffScreen = useCallback(() => {
    const xSign =
      swipeDataRef.current.swipeStatus === SwipeStatus.ACCEPTED ? 1 : -1;

    swipeDataRef.current.componentVelocityX = flyOutSpeedXPx * xSign;
    swipeDataRef.current.componentVelocityY = flyOutSpeedYPx;

    swipeDataRef.current.componentCurrentX +=
      swipeDataRef.current.componentVelocityX;
    swipeDataRef.current.componentCurrentY +=
      swipeDataRef.current.componentVelocityY;
  }, [flyOutSpeedXPx, flyOutSpeedYPx]);

  const updateSwipeData = useCallback(
    (dt: number) => {
      updateSwipeThresholdData();
      updateElementOpacityData();

      if (swipeDataRef.current.mouseDown) {
        moveElementToBeUnderCursor();
      } else if (swipeDataRef.current.swipeStatus === SwipeStatus.UNDECIDED) {
        moveElementTowardsCenter(dt);
      } else {
        moveElementOffScreen();
      }
    },
    [
      updateSwipeThresholdData,
      updateElementOpacityData,
      moveElementToBeUnderCursor,
      moveElementOffScreen,
      moveElementTowardsCenter,
    ]
  );

  /** Sets `transform` styles on element based on the current swipe data */
  const applyDivStyles = useCallback(() => {
    if (!elementRef.current) return;

    elementRef.current.dataset.componentCurrentX = Math.abs(
      swipeDataRef.current.componentCurrentX
    ).toString();

    // update position and rotation of component
    const translate = `translate(${swipeDataRef.current.componentCurrentX}px, ${swipeDataRef.current.componentCurrentY}px)`;
    const rotate = `rotate(${
      swipeDataRef.current.componentCurrentX * rotationMultiplier
    }deg)`;
    const transform = `${translate} ${rotate}`;
    elementRef.current.dataset.mouseDx =
      swipeDataRef.current.mouseDx.toString();

    elementRef.current.style.transform = transform;
    elementRef.current.style.opacity =
      swipeDataRef.current.componentOpacity.toString();

    if (debugSwipeStatusDecisionThreshold) {
      switch (swipeDataRef.current.swipeStatus) {
        case SwipeStatus.ACCEPTED:
          elementRef.current.style.backgroundColor = "lightgreen";
          break;
        case SwipeStatus.REJECTED:
          elementRef.current.style.backgroundColor = "pink";
          break;
        default:
          elementRef.current.style.backgroundColor = "lightblue";
      }
    }
  }, [rotationMultiplier, debugSwipeStatusDecisionThreshold]);

  const animate = useCallback(
    (timestamp: DOMHighResTimeStamp) => {
      // exit early if the swipe status has been decided and component has finished getting dismissed
      if (
        !swipeDataRef.current.componentWithinViewport &&
        swipeDataRef.current.mouseDown === false &&
        swipeDataRef.current.swipeStatus !== SwipeStatus.UNDECIDED
      ) {
        onExitComplete?.(swipeDataRef.current);
        return;
      }

      // for the sake of testing, considers a card exited if its
      // status has been decided (via button press)
      if (
        process.env.NODE_ENV === "test" &&
        swipeDataRef.current.swipeStatus !== SwipeStatus.UNDECIDED
      ) {
        onExitComplete?.(swipeDataRef.current);
        return;
      }

      // prevent against infinite animation loop
      if (Math.abs(swipeDataRef.current.componentCurrentX) > 1000) {
        console.log("infinite animation loop detected");
        onExitComplete?.(swipeDataRef.current);
        return;
      }

      // get time since last `animate` call
      swipeDataRef.current.timestampPrev =
        swipeDataRef.current.timestampCurrent;
      swipeDataRef.current.timestampCurrent = timestamp;

      // if a user goes to a different tab and then comes back, the time delta
      // can get quite large. This prevents `dt` from getting too big and messing
      // with component velocity when the user finally comes back to the tab
      const MAX_TIME_DIFF = 1000;
      const dt = Math.min(
        swipeDataRef.current.timestampCurrent -
          swipeDataRef.current.timestampPrev,
        MAX_TIME_DIFF
      );

      updateSwipeData(dt);
      applyDivStyles();
      onAnimateCycleComplete?.(swipeDataRef.current);

      animationHandleRef.current = requestAnimationFrame(animate);
    },
    [updateSwipeData, applyDivStyles, onExitComplete, onAnimateCycleComplete]
  );

  const stopAnimating = useCallback(() => {
    if (animationHandleRef.current !== NULL_ANIMATION_HANDLE) {
      cancelAnimationFrame(animationHandleRef.current);
      animationHandleRef.current = NULL_ANIMATION_HANDLE;
    }
  }, []);

  const startAnimating = useCallback(() => {
    // cancel any previously scheduled animations so that we only have
    // one active request at any one time
    stopAnimating();
    animationHandleRef.current = requestAnimationFrame(animate);
  }, [stopAnimating, animate]);

  /** Prevent animations from running when tab is hidden or window is minimized */
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === "visible") {
      startAnimating();
    } else {
      stopAnimating();
    }
  }, [stopAnimating, startAnimating]);

  /**
   * Keeping listeners in an object allows for easier, programmatic
   * adding and removing of event listeners to raw DOM objects.
   */
  const windowListeners = useMemo(
    () => ({
      mousemove: handleCursorMove,
      touchmove: handleTouchMove,
      contextmenu: handleCursorUp,
      blur: handleCursorUp,
      mouseup: handleCursorUp,
    }),
    [handleCursorMove, handleCursorUp, handleTouchMove]
  );

  /**
   * Keeping listeners in an object allows for easier, programmatic
   * adding and removing of event listeners to raw DOM objects.
   */
  const elementListeners = useMemo(
    () => ({
      mousedown: handleCursorDown,
      touchstart: handleTouchStart,
      touchend: handleCursorUp,
      mouseup: handleCursorUp,
    }),
    [handleCursorDown, handleTouchStart, handleCursorUp]
  );

  const addAllListeners = useCallback(() => {
    Object.entries(elementListeners).forEach(([e, cb]) =>
      // can't prove this is correct without doing very hacky TS types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      elementRef.current?.addEventListener(e, cb as any)
    );
    document.addEventListener("visibilitychange", handleVisibilityChange);
    Object.entries(windowListeners).forEach(([e, cb]) =>
      // can't prove this is correct without doing very hacky TS types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      window.addEventListener(e, cb as any)
    );
    if (elementRef.current) {
      intersectionObserverRef.current.observe(elementRef.current);
    }
  }, [windowListeners, elementListeners, handleVisibilityChange]);

  const removeAllListeners = useCallback(() => {
    Object.entries(elementListeners).forEach(([e, cb]) =>
      // can't prove this is correct without doing very hacky TS types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      elementRef.current?.removeEventListener(e, cb as any)
    );
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    Object.entries(windowListeners).forEach(([e, cb]) =>
      // can't prove this is correct without doing very hacky TS types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      window.removeEventListener(e, cb as any)
    );
    if (elementRef.current) {
      intersectionObserverRef.current.unobserve(elementRef.current);
    }
  }, [windowListeners, elementListeners, handleVisibilityChange]);

  const accept = useCallback(() => {
    // these functions are exclusively called as click handlers for now
    setAndEmitSwipeStatus(SwipeStatus.ACCEPTED, "click");
  }, [setAndEmitSwipeStatus]);

  const reject = useCallback(() => {
    // these functions are exclusively called as click handlers for now
    setAndEmitSwipeStatus(SwipeStatus.REJECTED, "click");
  }, [setAndEmitSwipeStatus]);

  const reset = useCallback(() => {
    swipeDataRef.current = { ...DefaultSwipeData };
    startAnimating();
  }, [startAnimating]);

  useEffect(() => {
    addAllListeners();
    startAnimating();

    return () => {
      removeAllListeners();
      stopAnimating();
    };
  }, [
    addAllListeners,
    removeAllListeners,
    animate,
    stopAnimating,
    startAnimating,
  ]);

  const result = useMemo(
    () => ({
      /** Immediately moves SwipeStatus state to `SwipeStatus.ACCEPTED` and dismisses the element */
      accept,
      /** Immediately moves SwipeStatus state to `SwipeStatus.REJECTED` and dismisses the element */
      reject,
      /** Resets the state of the card--useful for when there is an API error when trying to swipe */
      reset,
      /** This value must be assigned to the element that is being animated */
      ref: elementRef,
      /** Stateful value that returns whether card is currently being swiped or not */
      swiping,
    }),
    [accept, reset, reject, swiping]
  );

  return result;
}
